import { createContext, useContext, useEffect, useState } from 'react'

type Theme = 'light' | 'dark'
type ThemePreference = Theme | 'system'

interface ThemeContextType {
  theme: Theme // The actual theme being applied
  themePreference: ThemePreference // The user's preference (may be 'system')
  setTheme: (theme: ThemePreference) => void
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

// Storage key for theme preference
const THEME_STORAGE_KEY = 'theme-preference'

// Helper to get the initial theme preference from localStorage
function getInitialThemePreference(): ThemePreference {
  if (typeof window === 'undefined') {
    return 'system'
  }

  const stored = localStorage.getItem(THEME_STORAGE_KEY) as ThemePreference | null
  if (stored && (stored === 'light' || stored === 'dark' || stored === 'system')) {
    return stored
  }

  return 'dark'
}

// Helper to resolve theme preference to actual theme
function resolveTheme(preference: ThemePreference): Theme {
  if (preference === 'system') {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return 'light'
  }
  return preference
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(getInitialThemePreference)
  const [theme, setThemeState] = useState<Theme>(() => resolveTheme(getInitialThemePreference()))

  // Apply theme to DOM whenever theme changes
  useEffect(() => {
    const root = window.document.documentElement

    // Remove previous theme classes
    root.classList.remove('light', 'dark')

    // Add new theme class
    root.classList.add(theme)

    // Store preference in localStorage
    localStorage.setItem(THEME_STORAGE_KEY, themePreference)
  }, [theme, themePreference])

  // Listen for system preference changes
  useEffect(() => {
    // Only need to listen if user prefers 'system' mode
    if (themePreference !== 'system') {
      return
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      const newTheme = e.matches ? 'dark' : 'light'
      setThemeState(newTheme)
    }

    // Modern browsers
    if (mediaQuery.addEventListener !== undefined) {
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
    // Legacy support
    else if (mediaQuery.addListener !== undefined) {
      mediaQuery.addListener(handleChange)
      return () => mediaQuery.removeListener(handleChange)
    }

    // Fallback - return empty function
    return () => {}
  }, [themePreference])

  const setTheme = (newTheme: ThemePreference) => {
    setThemePreferenceState(newTheme)
    setThemeState(resolveTheme(newTheme))
  }

  const toggleTheme = () => {
    // Toggle between light and dark, leaving system mode
    const newPreference: Theme = theme === 'light' ? 'dark' : 'light'
    setThemePreferenceState(newPreference)
    setThemeState(newPreference)
  }

  return (
    <ThemeContext.Provider value={{ theme, themePreference, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
